import React, {useContext} from 'react';
import './SelectBox.scss';
import {FormControl, InputLabel, Select} from '@mui/material'
import {ThemeProvider} from "@mui/material/styles";
import {InputTheme} from "../InputTheme";
import {map} from "lodash";
import {I18nReactContext} from "../../../contexts/I18nContext";

/**
 * A reusable select box component that renders a Material-UI Select with customizable options.
 *
 * @param {object} props - The component props
 * @param {string} [props.className] - Additional CSS class name for the select box container
 * @param {string} props.label - The label text for the select input
 * @param {string|number} props.value - The currently selected value
 * @param {function} props.onChange - Callback function when selection changes
 * @param {Array<{value: string|number, text?: string, textKey?: string, disabled?: boolean, textOnHover?: string}>} props.options - Array of options to display
 * @param {object} [props.inputProps] - Additional props to pass to the underlying input element
 * @param {boolean} [props.disabled] - Whether the select is disabled
 * @param {boolean} [props.required] - Whether the select is required
 * @param {string} [props.dataTestId] - Test ID for testing purposes
 * @returns {JSX.Element} A Material-UI Select component wrapped in a ThemeProvider
 */
export const SelectBox = ({className, label, value, onChange, options, inputProps, disabled, required, dataTestId}) => {
    const {i18n} = useContext(I18nReactContext);
    return <ThemeProvider theme={InputTheme}>
        <div className={`select-box ${className}`}>
            <FormControl>
                <InputLabel id={`label-${label}`}>{label}</InputLabel>
                <Select
                    labelId={`label-${label}`}
                    label={label}
                    native
                    MenuProps={{
                        variant: 'menu'
                    }}
                    disabled={disabled}
                    value={value}
                    className="select-input"
                    inputProps={{ ...inputProps, 'data-testid': dataTestId }}
                    onChange={onChange}>
                    {map(options, (option) => {
                        return <option
                            disabled={option.disabled}
                            title={option.textOnHover}
                            key={option.value}
                            value={option.value}>
                            {option.text || i18n.translate(option.textKey)}
                        </option>
                    })}
                </Select>
            </FormControl>
        </div>
    </ThemeProvider>
}
